import { memo, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { ChatConfigForm } from './ChatConfigForm';
import { ChatConfigReadOnly } from './ChatConfigReadOnly';

const DEFAULT_MESSAGE = 'Hi, I\'m a digital twin of this HCP segment. I can help you understand how this segment thinks, what evidence matters most, and how they may respond to different messages. Ask me anything!';

const DEFAULT_QUESTIONS = [
  'What are the top clinical priorities for this segment?',
  'How does this segment typically evaluate new treatment options?',
  'What types of evidence would resonate most with this segment?',
  'What are the most likely objections from this segment to our message?',
];

type SegmentChatConfigProps = {
  initialMessage: string | null | undefined;
  questions: string[] | null | undefined;
  onSave: (data: { initialMessage: string | null; questions: string[] | null }) => Promise<void>;
  isSaving?: boolean;
  whitelabelEnabled?: boolean;
  primaryColor?: string;
};

const SegmentInitialChatConfig = ({
  initialMessage,
  questions,
  onSave,
  isSaving = false,
  whitelabelEnabled,
  primaryColor,
}: SegmentChatConfigProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const displayMessage = initialMessage ?? DEFAULT_MESSAGE;
  // null = never configured → show defaults; [] = explicitly cleared → show nothing
  const displayQuestions = questions === null || questions === undefined ? DEFAULT_QUESTIONS : questions;

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(async (data: { initialMessage: string | null; questions: string[] }) => {
    await onSave({ initialMessage: data.initialMessage, questions: data.questions });
    setIsEditing(false);
  }, [onSave]);

  return (
    <ChatConfigSection>
      {isEditing ? (
        <ChatConfigForm
          initialMessage={displayMessage}
          initialQuestions={displayQuestions}
          onSave={handleSave}
          onCancel={handleCancel}
          isSaving={isSaving}
        />
      ) : (
        <ChatConfigReadOnly
          initialMessage={displayMessage}
          questions={displayQuestions}
          onEdit={handleEdit}
          whitelabelEnabled={whitelabelEnabled}
          primaryColor={primaryColor}
        />
      )}
    </ChatConfigSection>
  );
};

export const SegmentChatConfig = memo(SegmentInitialChatConfig);

const ChatConfigSection = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--gray-l);
`;
